/**
 * SLAPill Component
 * Displays remaining SLA time with color-coded urgency bands.
 */

import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';
import type { SLABand } from '../../types/tasks';

export interface SLAPillProps {
  daysRemaining: number | null;
  slaBand: SLABand | null;
  compact?: boolean;
}

interface BandStyle {
  bg: string;
  color: string;
}

const BAND_STYLES: Record<SLABand, BandStyle> = {
  // success.bg is #f0fdf4 (lighter) — using Tailwind emerald-100 for stronger pill contrast
  green: { bg: '#d1fae5', color: theme.colors.status.success.text },
  // warning.bg is #fffbeb (lighter) — using Tailwind amber-100 for stronger pill contrast
  yellow: { bg: theme.colors.status.warning.bg, color: '#92400e' },
  // error.bg is #fef2f2 (lighter) — using Tailwind red-100 for stronger pill contrast
  red: { bg: '#fee2e2', color: '#991b1b' },
};

const Pill = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};

  ${({ $bg, $color }) => css`
    background: ${$bg};
    color: ${$color};
  `}
`;

function formatLabel(daysRemaining: number, compact: boolean): string {
  if (daysRemaining < 0) {
    const days = Math.abs(daysRemaining);
    return compact ? `-${days}d` : `${days}d overdue`;
  }
  if (daysRemaining === 0) {
    return compact ? 'Due' : 'Due today';
  }
  return compact ? `${daysRemaining}d` : `${daysRemaining}d left`;
}

export function SLAPill({ daysRemaining, slaBand, compact = false }: SLAPillProps) {
  if (daysRemaining === null || slaBand === null) {
    return null;
  }

  const bandStyle = BAND_STYLES[slaBand];
  const label = formatLabel(daysRemaining, compact);

  return (
    <Pill $bg={bandStyle.bg} $color={bandStyle.color} aria-label={`Resolution timeline: ${label}`}>
      {label}
    </Pill>
  );
}

export default SLAPill;
